<script setup lang="ts">
defineOptions({ inheritAttrs: false })

const attrs = useAttrs()

const props = withDefaults(defineProps<{
  modelValue?: boolean
  rail?: boolean
  permanent?: boolean
  temporary?: boolean
  width?: number | string
  railWidth?: number
  location?: 'start' | 'end' | 'left' | 'right'
  floating?: boolean
  color?: string
}>(), {
  modelValue: true,
  width: 280,
  railWidth: 72,
  location: 'start',
})

const emit = defineEmits<{
  'update:modelValue': [val: boolean]
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
})

const normalizedLocation = computed(() => {
  if (props.location === 'right') return 'end'
  if (props.location === 'left') return 'start'
  return props.location
})

const computedWidth = computed(() => {
  if (!isOpen.value && !props.permanent) return '0'
  if (props.rail) return `${props.railWidth}px`
  const w = props.width
  if (typeof w === 'number') return `${w}px`
  if (typeof w === 'string' && /^\d+$/.test(w)) return `${w}px`
  return w
})
</script>

<template>
  <aside
    v-bind="attrs"
    class="m-nav-drawer"
    :class="[
      `m-nav-drawer--${normalizedLocation}`,
      {
        'm-nav-drawer--open': isOpen,
        'm-nav-drawer--rail': rail,
        'm-nav-drawer--temporary': temporary,
        'm-nav-drawer--permanent': permanent,
        'm-nav-drawer--floating': floating,
      }
    ]"
    :style="{ width: computedWidth }"
  >
    <div class="m-nav-drawer__content">
      <slot />
    </div>
  </aside>

  <div
    v-if="temporary && isOpen"
    class="m-nav-drawer__scrim"
    @click="isOpen = false"
  />
</template>

<style scoped>
.m-nav-drawer {
  display: flex;
  flex-direction: column;
  background: var(--m-surface);
  overflow-x: hidden;
  overflow-y: auto;
  transition: width var(--m-transition-slow);
  z-index: var(--m-z-drawer);
  flex-shrink: 0;
  height: 100%;
}

.m-nav-drawer--temporary {
  position: fixed;
  top: 0;
  bottom: 0;
  box-shadow: var(--m-shadow-xl);
}
.m-nav-drawer--temporary.m-nav-drawer--start { left: 0; }
.m-nav-drawer--temporary.m-nav-drawer--end { right: 0; }

.m-nav-drawer--floating { border: none; }
.m-nav-drawer:not(.m-nav-drawer--floating) {
  border-right: 1px solid var(--m-border-light);
}
.m-nav-drawer--end:not(.m-nav-drawer--floating) {
  border-right: none;
  border-left: 1px solid var(--m-border-light);
}

.m-nav-drawer--rail { overflow: hidden; }
.m-nav-drawer--rail .m-nav-drawer__content { align-items: center; }

.m-nav-drawer__content {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: var(--m-space-2) 0;
}

.m-nav-drawer__scrim {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: calc(var(--m-z-drawer) - 1);
}
</style>
